import { Timestamp } from 'firebase/firestore';
import {
  clampMatchEventRounds,
  MATCH_EVENT_TYPE_OPTIONS,
  matchEventTypeOptionFor,
  matchRulesForEventType,
} from '../constants/matchEventTypes';
import { currentPreRiftSetCode } from './preRift';
import { normalizedLegendLabel } from './championIdentity';
import { clampTargetScore, DEFAULT_TARGET_SCORE, historyEntryIsDraw, isUnlabeledDeckTitle } from './matchTracker';
import type { MatchBestOf, MatchGameResult, MatchRules, MatchScoreMap, UserMatchHistoryEntry } from '../types/match';
import type {
  EventPlayerDeck,
  MatchEvent,
  MatchEventRecord,
  MatchEventRound,
  MatchEventType,
} from '../types/matchEvent';

export type { EventPlayerDeck };

export const MATCH_EVENT_DRAFT_STORAGE_KEY = 'riftbound-match-event-v1';
export const MATCH_EVENT_VERSION = 1;

export function emptyRound(
  roundNumber: number,
  bestOf: MatchBestOf,
  deck?: EventPlayerDeck | null,
): MatchEventRound {
  return {
    roundNumber,
    status: 'pending',
    matchResultId: null,
    opponentName: null,
    opponentLegendName: null,
    opponentLegendImageUrl: null,
    opponentDeckLabel: null,
    myDeckId: deck?.myDeckId ?? null,
    myDeckTitle: deck?.myDeckTitle ?? null,
    myLegendName: deck?.myLegendName ?? null,
    myLegendImageUrl: deck?.myLegendImageUrl ?? null,
    myGameWins: 0,
    opponentGameWins: 0,
    didWin: null,
    bestOf,
    gameResults: [],
    durationSeconds: null,
    completedAt: null,
  };
}

export function eventPlayerDeck(event: Pick<MatchEvent, keyof EventPlayerDeck>): EventPlayerDeck {
  return {
    myDeckId: event.myDeckId ?? null,
    myDeckTitle: event.myDeckTitle ?? null,
    myLegendName: event.myLegendName ?? null,
    myLegendImageUrl: event.myLegendImageUrl ?? null,
  };
}

export function hasEventPlayerDeck(deck: Partial<EventPlayerDeck> | null | undefined): boolean {
  return Boolean(deck?.myDeckId || deck?.myDeckTitle || deck?.myLegendName || deck?.myLegendImageUrl);
}

export function eventPlayerDeckHistoryFields(deck: EventPlayerDeck): {
  myDeckId: string | null;
  myDeckTitle: string | null;
  myDeckCoverUrl: string | null;
} {
  return {
    myDeckId: deck.myDeckId,
    myDeckTitle: deck.myDeckTitle || deck.myLegendName,
    myDeckCoverUrl: deck.myLegendImageUrl,
  };
}

/**
 * Deck identity plus attached result ids. Used so a stamp that runs when the
 * legend is first chosen (no results yet) does not skip the later sync that
 * actually has `matchResultId`s to rewrite.
 */
export function eventHistoryStampKey(event: MatchEvent): string {
  const resultIds = event.rounds
    .map((round) => round.matchResultId)
    .filter((id): id is string => Boolean(id))
    .join(',');
  return `${event.id}:${event.myDeckId ?? ''}|${event.myDeckTitle ?? ''}|${event.myLegendName ?? ''}|${event.myLegendImageUrl ?? ''}|${resultIds}`;
}

function mergeEventPlayerDeck(event: MatchEvent, patch: Partial<EventPlayerDeck>): EventPlayerDeck {
  return {
    myDeckId: patch.myDeckId !== undefined ? patch.myDeckId : (event.myDeckId ?? null),
    myDeckTitle: deckTitleOrNull(patch.myDeckTitle !== undefined ? patch.myDeckTitle : event.myDeckTitle),
    myLegendName: patch.myLegendName !== undefined ? patch.myLegendName : (event.myLegendName ?? null),
    myLegendImageUrl: patch.myLegendImageUrl !== undefined ? patch.myLegendImageUrl : (event.myLegendImageUrl ?? null),
  };
}

function deckTitleOrNull(value: string | null | undefined): string | null {
  const trimmed = value?.trim() || null;
  if (!trimmed || isUnlabeledDeckTitle(trimmed)) return null;
  return trimmed;
}

/** Stamp the event-level deck onto every round. Later edits rewrite the whole night. */
export function setEventPlayerDeck(event: MatchEvent, patch: Partial<EventPlayerDeck>): MatchEvent {
  const deck = mergeEventPlayerDeck(event, patch);
  return {
    ...event,
    ...deck,
    revision: event.revision + 1,
    updatedAt: Timestamp.now(),
    rounds: event.rounds.map((round) => ({ ...round, ...deck })),
  };
}

function normalizeEventSetCode(value: string | null | undefined): string | null {
  const trimmed = value?.trim().toUpperCase() || '';
  return trimmed || null;
}

/** Pre-Rift stores its sealed expansion. Every other event type leaves it empty. */
function sealedSetCodeForType(type: string, setCode: string | null | undefined): string | null {
  if (type !== 'prerelease') return null;
  return normalizeEventSetCode(setCode) ?? currentPreRiftSetCode();
}

export interface CreateMatchEventInput extends Partial<MatchRules>, Partial<EventPlayerDeck> {
  id: string;
  ownerUid: string;
  type: MatchEventType;
  name?: string;
  /** Pre-Rift only. Omitted values use the expansion that is current that day. */
  setCode?: string | null;
  storeName?: string | null;
  plannedRounds?: number;
  playerCount?: number | null;
}

export function createMatchEvent(input: CreateMatchEventInput): MatchEvent {
  const option = matchEventTypeOptionFor(input.type);
  const now = Timestamp.now();
  const bestOf = input.bestOf ?? option.recommendedBestOf;
  const plannedRounds = clampMatchEventRounds(input.plannedRounds ?? option.defaultRounds);
  const deck: EventPlayerDeck = {
    myDeckId: input.myDeckId ?? null,
    myDeckTitle: deckTitleOrNull(input.myDeckTitle),
    myLegendName: input.myLegendName ?? null,
    myLegendImageUrl: input.myLegendImageUrl ?? null,
  };

  return {
    v: MATCH_EVENT_VERSION,
    revision: 1,
    id: input.id,
    ownerUid: input.ownerUid,
    type: input.type,
    name: input.name?.trim() || option.label,
    setCode: sealedSetCodeForType(input.type, input.setCode),
    storeName: input.storeName?.trim() || null,
    opLevel: option.opLevel,
    format: input.format ?? option.format,
    bestOf,
    targetScore: clampTargetScore(input.targetScore ?? DEFAULT_TARGET_SCORE),
    plannedRounds,
    playerCount: input.playerCount ?? null,
    status: 'active',
    ...deck,
    rounds: Array.from({ length: plannedRounds }, (_, index) => emptyRound(index + 1, bestOf, deck)),
    wins: 0,
    losses: 0,
    draws: 0,
    posterNote: null,
    startedAt: now,
    updatedAt: now,
    completedAt: null,
  };
}

export function matchEventRecord(event: MatchEvent): MatchEventRecord {
  return event.rounds.reduce<MatchEventRecord>(
    (record, round) => {
      if (round.status === 'completed') {
        if (round.didWin === true) record.wins += 1;
        else if (round.didWin === false) record.losses += 1;
        else record.draws += 1;
      } else if (round.status === 'draw') {
        record.draws += 1;
      } else if (round.status === 'bye') {
        record.wins += 1;
      }
      return record;
    },
    { wins: 0, losses: 0, draws: 0 },
  );
}

export function formatMatchEventRecord(record: MatchEventRecord): string {
  return record.draws > 0
    ? `${record.wins}-${record.losses}-${record.draws}`
    : `${record.wins}-${record.losses}`;
}

/** Rounds that have a result — the ones the poster and stats care about. */
export function playedRounds(event: MatchEvent): MatchEventRound[] {
  return event.rounds.filter((round) => round.status !== 'pending' && round.status !== 'inProgress');
}

export function matchEventRoundOpponentLabel(round: MatchEventRound): string {
  if (round.status === 'bye') return 'Bye';
  return normalizedLegendLabel(round.opponentLegendName)
    || normalizedLegendLabel(round.opponentDeckLabel)
    || round.opponentName?.trim()
    || (round.opponentDeckLabel && !isUnlabeledDeckTitle(round.opponentDeckLabel)
      ? round.opponentDeckLabel.trim()
      : null)
    || `Round ${round.roundNumber}`;
}

export function matchEventRoundScoreLabel(round: MatchEventRound): string {
  if (round.status === 'bye') return 'BYE';
  if (round.status === 'draw') return 'DRAW';
  if (round.status === 'pending') return '';
  if (round.status === 'inProgress') return 'LIVE';
  return `${round.myGameWins}-${round.opponentGameWins}`;
}

export function matchEventRoundGamesLine(round: MatchEventRound): string | null {
  if (!round.gameResults.length) return null;
  return round.gameResults
    .map((game) => `G${game.gameNumber} ${game.scores.A ?? 0}–${game.scores.B ?? 0}`)
    .join('   ');
}

/** Short outcome caption for the score column; null when the score already says it. */
export function matchEventRoundOutcomeLabel(round: MatchEventRound): string | null {
  if (round.status === 'draw' || (round.status === 'completed' && round.didWin == null)) return 'Tied';
  if (round.didWin === true) return 'Won';
  if (round.didWin === false) return 'Lost';
  return null;
}

export function matchEventRoundScoreColor(
  round: MatchEventRound,
): 'success' | 'error' | 'accent' | 'textMuted' {
  if (round.didWin === true) return 'success';
  if (round.didWin === false) return 'error';
  if (round.status === 'inProgress') return 'accent';
  return 'textMuted';
}

export function nextPlayableRound(event: MatchEvent): MatchEventRound | null {
  return event.rounds.find((round) => round.status === 'pending' || round.status === 'inProgress') ?? null;
}

export function isMatchEventComplete(event: MatchEvent): boolean {
  return event.rounds.every((round) => round.status !== 'pending' && round.status !== 'inProgress');
}

/** The working copy of an event night that should still own the round board. */
export function isActiveMatchEvent(event: MatchEvent | null | undefined): event is MatchEvent {
  return event != null && event.status === 'active';
}

function withRecord(event: MatchEvent, rounds: MatchEventRound[]): MatchEvent {
  const next: MatchEvent = { ...event, rounds, revision: event.revision + 1, updatedAt: Timestamp.now() };
  const record = matchEventRecord(next);
  next.wins = record.wins;
  next.losses = record.losses;
  next.draws = record.draws;
  return next;
}

function replaceRound(
  event: MatchEvent,
  roundNumber: number,
  patch: Partial<MatchEventRound>,
): MatchEvent {
  const rounds = event.rounds.map((round) =>
    round.roundNumber === roundNumber ? { ...round, ...patch } : round,
  );
  return withRecord(event, rounds);
}

export interface RoundOpponentInput {
  opponentName: string | null;
  opponentLegendName: string | null;
  opponentLegendImageUrl: string | null;
  opponentDeckLabel: string | null;
}

export function setRoundOpponent(
  event: MatchEvent,
  roundNumber: number,
  opponent: RoundOpponentInput,
): MatchEvent {
  return replaceRound(event, roundNumber, {
    ...opponent,
    status: 'inProgress',
  });
}

export interface RoundOpponentPatch {
  opponentName?: string | null;
  opponentLegendName?: string | null;
  opponentLegendImageUrl?: string | null;
  opponentDeckLabel?: string | null;
}

/**
 * Rewrite one finished round's opponent after the night is logged.
 * Scores, status, and the player's legend stay as they were.
 */
export function patchEventRoundOpponent(
  event: MatchEvent,
  target: { roundNumber?: number | null; matchResultId?: string | null },
  patch: RoundOpponentPatch,
): MatchEvent {
  const roundNumber = target.roundNumber ?? null;
  const matchResultId = target.matchResultId?.trim() || null;
  const round = event.rounds.find((item) => (
    roundNumber != null
      ? item.roundNumber === roundNumber
      : Boolean(matchResultId) && item.matchResultId === matchResultId
  ));
  if (!round) return event;

  const next: Partial<MatchEventRound> = {};
  if (patch.opponentName !== undefined) next.opponentName = patch.opponentName;
  if (patch.opponentLegendName !== undefined) next.opponentLegendName = patch.opponentLegendName;
  if (patch.opponentLegendImageUrl !== undefined) next.opponentLegendImageUrl = patch.opponentLegendImageUrl;
  if (patch.opponentDeckLabel !== undefined) next.opponentDeckLabel = patch.opponentDeckLabel;
  if (Object.keys(next).length === 0) return event;
  return replaceRound(event, round.roundNumber, next);
}

/** History edit fields that belong on the event round the poster reads. */
export function opponentPatchFromHistory(changes: {
  opponentName?: string | null;
  opponentLegendName?: string | null;
  opponentDeckCoverUrl?: string | null;
  opponentDeckLabel?: string | null;
}): RoundOpponentPatch | null {
  const patch: RoundOpponentPatch = {};
  if (changes.opponentName !== undefined) patch.opponentName = changes.opponentName;
  if (changes.opponentLegendName !== undefined) patch.opponentLegendName = changes.opponentLegendName;
  if (changes.opponentDeckCoverUrl !== undefined) patch.opponentLegendImageUrl = changes.opponentDeckCoverUrl;
  if (changes.opponentDeckLabel !== undefined) patch.opponentDeckLabel = changes.opponentDeckLabel;
  return Object.keys(patch).length === 0 ? null : patch;
}

export interface RoundResultInput {
  matchResultId: string | null;
  didWin: boolean | null;
  myGameWins: number;
  opponentGameWins: number;
  bestOf: MatchBestOf;
  gameResults: MatchGameResult[];
  durationSeconds: number | null;
  myDeckId: string | null;
  myDeckTitle: string | null;
  myLegendName: string | null;
  myLegendImageUrl: string | null;
  opponentName?: string | null;
  opponentLegendName?: string | null;
  opponentLegendImageUrl?: string | null;
  opponentDeckLabel?: string | null;
}

export function recordRoundResult(
  event: MatchEvent,
  roundNumber: number,
  result: RoundResultInput,
): MatchEvent {
  const existing = event.rounds.find((round) => round.roundNumber === roundNumber);
  const incoming: EventPlayerDeck = {
    myDeckId: result.myDeckId,
    myDeckTitle: result.myDeckTitle,
    myLegendName: result.myLegendName,
    myLegendImageUrl: result.myLegendImageUrl,
  };
  const deck = hasEventPlayerDeck(event) ? eventPlayerDeck(event) : incoming;
  const stampAllRounds = hasEventPlayerDeck(deck);
  const completedAt = Timestamp.now();

  const rounds = event.rounds.map((round) => {
    const withDeck = stampAllRounds ? { ...round, ...deck } : round;
    if (round.roundNumber !== roundNumber) return withDeck;
    return {
      ...withDeck,
      status: result.didWin == null ? 'draw' as const : 'completed' as const,
      matchResultId: result.matchResultId,
      didWin: result.didWin,
      myGameWins: result.myGameWins,
      opponentGameWins: result.opponentGameWins,
      bestOf: result.bestOf,
      gameResults: result.gameResults,
      durationSeconds: result.durationSeconds,
      ...deck,
      opponentName: result.opponentName ?? existing?.opponentName ?? null,
      opponentLegendName: result.opponentLegendName ?? existing?.opponentLegendName ?? null,
      opponentLegendImageUrl: result.opponentLegendImageUrl ?? existing?.opponentLegendImageUrl ?? null,
      opponentDeckLabel: result.opponentDeckLabel ?? existing?.opponentDeckLabel ?? null,
      completedAt,
    };
  });

  const next = withRecord(event, rounds);
  return stampAllRounds ? { ...next, ...deck } : next;
}

export function markRoundBye(event: MatchEvent, roundNumber: number): MatchEvent {
  return replaceRound(event, roundNumber, {
    status: 'bye',
    didWin: null,
    opponentName: 'Bye',
    myGameWins: 0,
    opponentGameWins: 0,
    completedAt: Timestamp.now(),
  });
}

export function markRoundDraw(event: MatchEvent, roundNumber: number): MatchEvent {
  const round = event.rounds.find((item) => item.roundNumber === roundNumber);
  if (!round || round.status !== 'pending') return event;
  return replaceRound(event, roundNumber, {
    status: 'draw',
    didWin: null,
    completedAt: Timestamp.now(),
  });
}

export function resetRound(event: MatchEvent, roundNumber: number): MatchEvent {
  return replaceRound(event, roundNumber, emptyRound(roundNumber, event.bestOf, eventPlayerDeck(event)));
}

/** Drop an unfinished round back to pending without forgetting who they were playing. */
export function releaseInProgressRound(event: MatchEvent, roundNumber: number): MatchEvent {
  const round = event.rounds.find((item) => item.roundNumber === roundNumber);
  if (!round || round.status !== 'inProgress') return event;
  return replaceRound(event, roundNumber, { status: 'pending' });
}

const EMPTY_REVISION_SCORES: MatchScoreMap = { A: 0, B: 0, C: 0, D: 0 };

/** Counter state one step before a finished round ended, so it can be rescored. */
export interface RoundRevisionState {
  currentGame: number;
  myGameWins: number;
  opponentGameWins: number;
  victoryPoints: MatchScoreMap;
  gameResults: MatchGameResult[];
  durationSeconds: number;
}

function revisionWinnerSlot(round: MatchEventRound): 'A' | 'B' {
  const last = round.gameResults.at(-1);
  if (last?.winnerSlot === 'B') return 'B';
  if (last?.winnerSlot === 'A') return 'A';
  return round.didWin === false ? 'B' : 'A';
}

function scoresWithDecidingPointRemoved(
  scores: MatchScoreMap,
  winner: 'A' | 'B',
  targetScore: number,
): MatchScoreMap {
  const stored = scores[winner] ?? 0;
  const opened = stored >= targetScore ? Math.max(0, targetScore - 1) : Math.max(0, stored - 1);
  return { ...EMPTY_REVISION_SCORES, ...scores, [winner]: opened };
}

/**
 * Reopen a finished round in the score counter. A decided match loses its last
 * game win and the point that closed that game. A draw with no recorded games
 * opens as a fresh 0–0.
 */
export function revisionStateFromRound(
  round: MatchEventRound,
  targetScore: number,
): RoundRevisionState {
  const durationSeconds = Math.max(0, round.durationSeconds ?? 0);
  const games = round.gameResults ?? [];
  const isDraw = round.status === 'draw' || round.didWin == null;

  if (isDraw && games.length === 0) {
    return {
      currentGame: 1,
      myGameWins: 0,
      opponentGameWins: 0,
      victoryPoints: { ...EMPTY_REVISION_SCORES },
      gameResults: [],
      durationSeconds,
    };
  }

  if (isDraw) {
    const lastNumber = games.at(-1)?.gameNumber ?? games.length;
    return {
      currentGame: lastNumber + 1,
      myGameWins: round.myGameWins,
      opponentGameWins: round.opponentGameWins,
      victoryPoints: { ...EMPTY_REVISION_SCORES },
      gameResults: games,
      durationSeconds,
    };
  }

  const winner = revisionWinnerSlot(round);
  const last = games.at(-1);
  return {
    currentGame: last?.gameNumber ?? Math.max(1, round.myGameWins + round.opponentGameWins),
    myGameWins: Math.max(0, round.myGameWins - (winner === 'A' ? 1 : 0)),
    opponentGameWins: Math.max(0, round.opponentGameWins - (winner === 'B' ? 1 : 0)),
    victoryPoints: last
      ? scoresWithDecidingPointRemoved(last.scores, winner, targetScore)
      : { ...EMPTY_REVISION_SCORES },
    gameResults: last ? games.slice(0, -1) : [],
    durationSeconds,
  };
}

/** Lowest round count that still keeps every round with a result or a live table. */
export function minimumKeptEventRounds(event: MatchEvent): number {
  const protectedThrough = event.rounds.reduce(
    (highest, round) => (round.status === 'pending' ? highest : round.roundNumber),
    0,
  );
  return Math.max(1, protectedThrough);
}

export function setPlannedRounds(event: MatchEvent, plannedRounds: number): MatchEvent {
  const target = clampMatchEventRounds(plannedRounds);
  const current = event.rounds.length;
  if (target === current) return event;

  let rounds: MatchEventRound[];
  if (target > current) {
    rounds = [
      ...event.rounds,
      ...Array.from(
        { length: target - current },
        (_, index) => emptyRound(current + index + 1, event.bestOf, eventPlayerDeck(event)),
      ),
    ];
  } else {
    // Never drop a round that already has a result or a match in progress.
    rounds = event.rounds.slice(0, Math.max(target, minimumKeptEventRounds(event)));
  }

  return { ...withRecord(event, rounds), plannedRounds: rounds.length };
}

/** Re-apply setup from the event sheet without discarding played rounds. */
export function applyMatchEventSetup(
  event: MatchEvent,
  input: Pick<CreateMatchEventInput, 'type' | 'name' | 'storeName' | 'plannedRounds' | 'playerCount' | 'bestOf' | 'setCode'>
    & Partial<EventPlayerDeck>,
): MatchEvent {
  const option = matchEventTypeOptionFor(input.type);
  const rules = matchRulesForEventType(input.type);
  const bestOf = input.bestOf ?? rules.bestOf;
  const nextSetCode = sealedSetCodeForType(input.type, input.setCode);
  const poolChanged = input.type === 'prerelease' && (
    event.type !== 'prerelease' || normalizeEventSetCode(event.setCode) !== nextSetCode
  );
  const sized = setPlannedRounds(
    {
      ...event,
      type: input.type,
      name: input.name?.trim() || option.label,
      setCode: nextSetCode,
      storeName: input.storeName?.trim() || null,
      opLevel: option.opLevel,
      format: rules.format,
      bestOf,
      targetScore: rules.targetScore,
      playerCount: input.playerCount ?? null,
      updatedAt: Timestamp.now(),
      revision: event.revision + 1,
    },
    input.plannedRounds ?? event.plannedRounds,
  );
  const rounds = sized.rounds.map((round) => (
    round.status === 'pending' || round.status === 'inProgress'
      ? { ...round, bestOf }
      : round
  ));
  const base = { ...sized, rounds };
  if (poolChanged) {
    return setEventPlayerDeck(base, {
      myDeckId: null,
      myDeckTitle: null,
      myLegendName: null,
      myLegendImageUrl: null,
    });
  }
  const withDeck = hasEventPlayerDeck(input)
    ? setEventPlayerDeck(base, input)
    : base;

  return withDeck;
}

export function completeMatchEvent(event: MatchEvent): MatchEvent {
  const now = Timestamp.now();
  return {
    ...event,
    status: 'completed',
    revision: event.revision + 1,
    updatedAt: now,
    completedAt: now,
  };
}

/**
 * The legend the poster is anchored on. Swiss is one deck all night, so the
 * event-level identity wins; older drafts without that field still vote by round.
 */
export function matchEventPrimaryLegend(event: MatchEvent): {
  name: string | null;
  imageUrl: string | null;
} {
  const eventName = event.myLegendName
    ?? (isUnlabeledDeckTitle(event.myDeckTitle) ? null : event.myDeckTitle)
    ?? null;
  const eventImage = event.myLegendImageUrl ?? null;

  const counts = new Map<string, { count: number; imageUrl: string | null }>();
  for (const round of event.rounds) {
    if (!round.myLegendName) continue;
    const existing = counts.get(round.myLegendName);
    counts.set(round.myLegendName, {
      count: (existing?.count ?? 0) + 1,
      imageUrl: existing?.imageUrl ?? round.myLegendImageUrl,
    });
  }

  let bestName: string | null = null;
  let bestCount = 0;
  let bestImage: string | null = null;
  for (const [name, entry] of counts) {
    if (entry.count > bestCount) {
      bestCount = entry.count;
      bestName = name;
      bestImage = entry.imageUrl;
    }
  }

  const fallback = event.rounds.find((round) => round.myDeckTitle || round.myLegendImageUrl);
  return {
    name: eventName
      || bestName
      || (isUnlabeledDeckTitle(fallback?.myDeckTitle) ? null : (fallback?.myDeckTitle ?? null)),
    imageUrl: eventImage || bestImage || fallback?.myLegendImageUrl || null,
  };
}

/**
 * Poster / recap caption: normalized legend as the headline, named list
 * underneath only when it is a real deck title (not Untitled / a print name).
 */
export function matchEventPlayedAs(event: MatchEvent): {
  legendLabel: string | null;
  deckLabel: string | null;
} {
  const primary = matchEventPrimaryLegend(event);
  const explicitLegend = event.myLegendName?.trim()
    || event.rounds.map((round) => round.myLegendName?.trim()).find(Boolean)
    || null;
  const legendLabel = normalizedLegendLabel(primary.name)
    || normalizedLegendLabel(event.myLegendName)
    || event.rounds.map((round) => normalizedLegendLabel(round.myLegendName)).find(Boolean)
    || (explicitLegend && !isUnlabeledDeckTitle(explicitLegend) ? explicitLegend : null)
    || null;
  const deckRaw = [event.myDeckTitle, ...event.rounds.map((round) => round.myDeckTitle)]
    .map((title) => title?.trim() || null)
    .find((title) => title && !isUnlabeledDeckTitle(title) && !normalizedLegendLabel(title))
    ?? null;
  const deckLabel = deckRaw && deckRaw.toLowerCase() !== legendLabel?.toLowerCase()
    ? deckRaw
    : null;
  return { legendLabel, deckLabel };
}

export function matchEventDurationSeconds(event: MatchEvent): number {
  return event.rounds.reduce((total, round) => total + (round.durationSeconds ?? 0), 0);
}

function asBestOf(value: unknown): MatchBestOf {
  return value === 3 || value === 5 ? value : 1;
}

function isFirestoreTimestamp(value: unknown): value is Timestamp {
  return typeof value === 'object'
    && value != null
    && typeof (value as Timestamp).toMillis === 'function'
    && typeof (value as Timestamp).toDate === 'function';
}

export function coerceTimestamp(value: unknown): Timestamp | null {
  if (value == null) return null;
  if (value instanceof Timestamp) return value;
  if (typeof value === 'number' && Number.isFinite(value)) return Timestamp.fromMillis(value);
  if (isFirestoreTimestamp(value)) {
    const millis = value.toMillis();
    return Number.isFinite(millis) ? Timestamp.fromMillis(millis) : null;
  }
  if (typeof value === 'object') {
    const record = value as { seconds?: number; nanoseconds?: number; _seconds?: number; _nanoseconds?: number };
    const seconds = record.seconds ?? record._seconds;
    const nanoseconds = record.nanoseconds ?? record._nanoseconds ?? 0;
    if (typeof seconds === 'number' && Number.isFinite(seconds)) {
      return new Timestamp(seconds, nanoseconds);
    }
  }
  return null;
}

function omitUndefinedDeep(value: unknown): unknown {
  if (value === undefined) return undefined;
  if (value === null || typeof value !== 'object') return value;
  if (isFirestoreTimestamp(value)) return value;
  if (Array.isArray(value)) return value.map(omitUndefinedDeep);
  const output: Record<string, unknown> = {};
  for (const [key, entry] of Object.entries(value as Record<string, unknown>)) {
    if (entry === undefined) continue;
    output[key] = omitUndefinedDeep(entry);
  }
  return output;
}

/** Firestore rejects `undefined`; keep Timestamp instances intact. */
export function matchEventFirestoreData(event: MatchEvent, ownerUid: string): Record<string, unknown> {
  return omitUndefinedDeep({ ...event, ownerUid }) as Record<string, unknown>;
}

function matchEventTypeFromName(name: string | null | undefined): MatchEventType {
  const trimmed = name?.trim().toLowerCase() ?? '';
  return MATCH_EVENT_TYPE_OPTIONS.find((option) => option.label.toLowerCase() === trimmed)?.value
    ?? 'custom';
}

function hydrateRound(raw: unknown, index: number): MatchEventRound {
  const round = raw && typeof raw === 'object' ? raw as Record<string, unknown> : {};
  const status = round.status === 'inProgress'
    || round.status === 'completed'
    || round.status === 'draw'
    || round.status === 'bye'
    ? round.status
    : 'pending';
  const didWin = round.didWin === true || round.didWin === false ? round.didWin : null;
  return {
    roundNumber: typeof round.roundNumber === 'number' ? round.roundNumber : index + 1,
    status,
    matchResultId: typeof round.matchResultId === 'string' ? round.matchResultId : null,
    opponentName: typeof round.opponentName === 'string' ? round.opponentName : null,
    opponentLegendName: typeof round.opponentLegendName === 'string' ? round.opponentLegendName : null,
    opponentLegendImageUrl: typeof round.opponentLegendImageUrl === 'string' ? round.opponentLegendImageUrl : null,
    opponentDeckLabel: typeof round.opponentDeckLabel === 'string' ? round.opponentDeckLabel : null,
    myDeckId: typeof round.myDeckId === 'string' ? round.myDeckId : null,
    myDeckTitle: typeof round.myDeckTitle === 'string' ? round.myDeckTitle : null,
    myLegendName: typeof round.myLegendName === 'string' ? round.myLegendName : null,
    myLegendImageUrl: typeof round.myLegendImageUrl === 'string' ? round.myLegendImageUrl : null,
    myGameWins: typeof round.myGameWins === 'number' ? round.myGameWins : 0,
    opponentGameWins: typeof round.opponentGameWins === 'number' ? round.opponentGameWins : 0,
    didWin,
    bestOf: asBestOf(round.bestOf),
    gameResults: Array.isArray(round.gameResults)
      ? round.gameResults.map((game, gameIndex) => {
        const row = game && typeof game === 'object' ? game as Record<string, unknown> : {};
        return {
          gameNumber: typeof row.gameNumber === 'number' ? row.gameNumber : gameIndex + 1,
          winnerSlot: row.winnerSlot === 'B' || row.winnerSlot === 'C' || row.winnerSlot === 'D' ? row.winnerSlot : 'A',
          scores: row.scores && typeof row.scores === 'object' ? row.scores as MatchGameResult['scores'] : {},
          completedAt: coerceTimestamp(row.completedAt) ?? Timestamp.now(),
          ...(row.winnerTeamId === 'team1' || row.winnerTeamId === 'team2' || row.winnerTeamId === null
            ? { winnerTeamId: row.winnerTeamId }
            : {}),
        };
      })
      : [],
    durationSeconds: typeof round.durationSeconds === 'number' ? round.durationSeconds : null,
    completedAt: coerceTimestamp(round.completedAt),
  };
}

/**
 * Normalize a Firestore (or loosely-shaped) event so poster code can call
 * `startedAt.toMillis()` without crashing on maps, millis, or missing fields.
 */
export function hydrateMatchEvent(raw: unknown): MatchEvent | null {
  if (!raw || typeof raw !== 'object') return null;
  const candidate = raw as Record<string, unknown>;
  if (typeof candidate.id !== 'string' || !candidate.id) return null;
  if (!Array.isArray(candidate.rounds)) return null;
  if (typeof candidate.startedAt === 'number') return deserializeMatchEvent(raw);

  const startedAt = coerceTimestamp(candidate.startedAt);
  if (!startedAt) return null;
  const option = matchEventTypeOptionFor(typeof candidate.type === 'string' ? candidate.type : null);
  const rounds = candidate.rounds.map((round, index) => hydrateRound(round, index));
  const record = matchEventRecord({ rounds } as MatchEvent);
  const name = typeof candidate.name === 'string' && candidate.name.trim() ? candidate.name : option.label;

  return {
    v: MATCH_EVENT_VERSION,
    revision: typeof candidate.revision === 'number' ? candidate.revision : 1,
    id: candidate.id,
    ownerUid: typeof candidate.ownerUid === 'string' ? candidate.ownerUid : '',
    type: option.value,
    name,
    setCode: typeof candidate.setCode === 'string' && candidate.setCode.trim()
      ? candidate.setCode.trim().toUpperCase()
      : null,
    storeName: typeof candidate.storeName === 'string' ? candidate.storeName : null,
    opLevel: candidate.opLevel === 'competitive' || candidate.opLevel === 'professional'
      ? candidate.opLevel
      : option.opLevel,
    format: typeof candidate.format === 'string' && candidate.format ? candidate.format : option.format,
    bestOf: asBestOf(candidate.bestOf ?? option.recommendedBestOf),
    targetScore: clampTargetScore(typeof candidate.targetScore === 'number' ? candidate.targetScore : DEFAULT_TARGET_SCORE),
    plannedRounds: clampMatchEventRounds(
      typeof candidate.plannedRounds === 'number' ? candidate.plannedRounds : rounds.length,
    ),
    playerCount: typeof candidate.playerCount === 'number' ? candidate.playerCount : null,
    status: candidate.status === 'completed' || candidate.status === 'abandoned' ? candidate.status : 'active',
    myDeckId: typeof candidate.myDeckId === 'string' ? candidate.myDeckId : null,
    myDeckTitle: typeof candidate.myDeckTitle === 'string' ? candidate.myDeckTitle : null,
    myLegendName: typeof candidate.myLegendName === 'string' ? candidate.myLegendName : null,
    myLegendImageUrl: typeof candidate.myLegendImageUrl === 'string' ? candidate.myLegendImageUrl : null,
    rounds,
    wins: record.wins,
    losses: record.losses,
    draws: record.draws,
    posterNote: typeof candidate.posterNote === 'string' ? candidate.posterNote : null,
    startedAt,
    updatedAt: coerceTimestamp(candidate.updatedAt) ?? startedAt,
    completedAt: coerceTimestamp(candidate.completedAt),
  };
}

/**
 * Recap for a night whose event doc never landed in Firestore — the round
 * history rows still carry `eventId`, so the poster can be rebuilt from them.
 */
export function matchEventFromHistoryEntries(
  eventId: string,
  ownerUid: string,
  entries: UserMatchHistoryEntry[],
): MatchEvent | null {
  if (!eventId || entries.length === 0) return null;

  const sorted = [...entries].sort((a, b) => {
    const roundA = a.eventRound ?? Number.MAX_SAFE_INTEGER;
    const roundB = b.eventRound ?? Number.MAX_SAFE_INTEGER;
    if (roundA !== roundB) return roundA - roundB;
    return (coerceTimestamp(a.playedAt)?.toMillis() ?? 0) - (coerceTimestamp(b.playedAt)?.toMillis() ?? 0);
  });

  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  const name = sorted.map((entry) => entry.eventName?.trim()).find(Boolean) || 'Event';
  const type = matchEventTypeFromName(name);
  const option = matchEventTypeOptionFor(type);
  const startedAt = coerceTimestamp(first.playedAt) ?? Timestamp.now();
  const completedAt = coerceTimestamp(last.playedAt) ?? startedAt;
  const rounds: MatchEventRound[] = sorted.map((entry, index) => ({
    roundNumber: entry.eventRound ?? index + 1,
    status: historyEntryIsDraw(entry) ? 'draw' : 'completed',
    matchResultId: entry.matchResultId,
    opponentName: entry.opponentName ?? null,
    opponentLegendName: entry.opponentLegendName ?? null,
    opponentLegendImageUrl: entry.opponentDeckCoverUrl ?? null,
    opponentDeckLabel: entry.opponentDeckLabel ?? null,
    myDeckId: entry.myDeckId ?? null,
    myDeckTitle: entry.myDeckTitle ?? null,
    myLegendName: null,
    myLegendImageUrl: entry.myDeckCoverUrl ?? null,
    myGameWins: entry.myGameWins ?? (entry.didWin === true ? 1 : 0),
    opponentGameWins: entry.opponentGameWins ?? (entry.didWin === true ? 0 : 1),
    didWin: historyEntryIsDraw(entry) ? null : entry.didWin,
    bestOf: asBestOf(entry.bestOf),
    gameResults: [],
    durationSeconds: null,
    completedAt: coerceTimestamp(entry.playedAt),
  }));

  const event: MatchEvent = {
    v: MATCH_EVENT_VERSION,
    revision: 1,
    id: eventId,
    ownerUid,
    type,
    name,
    setCode: type === 'prerelease'
      ? currentPreRiftSetCode(startedAt.toDate().toISOString().slice(0, 10))
      : null,
    storeName: sorted.map((entry) => entry.storeName?.trim()).find(Boolean) || null,
    opLevel: option.opLevel,
    format: first.format || option.format,
    bestOf: asBestOf(first.bestOf ?? option.recommendedBestOf),
    targetScore: clampTargetScore(first.targetScore ?? DEFAULT_TARGET_SCORE),
    plannedRounds: rounds.length,
    playerCount: null,
    status: 'completed',
    myDeckId: first.myDeckId ?? null,
    myDeckTitle: first.myDeckTitle ?? null,
    myLegendName: null,
    myLegendImageUrl: first.myDeckCoverUrl ?? null,
    rounds,
    wins: 0,
    losses: 0,
    draws: 0,
    posterNote: null,
    startedAt,
    updatedAt: completedAt,
    completedAt,
  };
  const record = matchEventRecord(event);
  return { ...event, wins: record.wins, losses: record.losses, draws: record.draws };
}

// ─── localStorage serialization ──────────────────────────────────────────────
// Timestamps are not JSON-safe, so the draft stores epoch millis and rebuilds
// them on read. Anything unparseable is discarded rather than half-restored.

type SerializedTimestamp = number | null;

interface SerializedRound extends Omit<MatchEventRound, 'completedAt' | 'gameResults'> {
  completedAt: SerializedTimestamp;
  gameResults: (Omit<MatchGameResult, 'completedAt'> & { completedAt: SerializedTimestamp })[];
}

interface SerializedMatchEvent
  extends Omit<MatchEvent, 'rounds' | 'startedAt' | 'updatedAt' | 'completedAt'> {
  rounds: SerializedRound[];
  startedAt: number;
  updatedAt: number;
  completedAt: SerializedTimestamp;
}

function toMillis(value: Timestamp | null): SerializedTimestamp {
  return value ? value.toMillis() : null;
}

function fromMillis(value: SerializedTimestamp): Timestamp | null {
  return typeof value === 'number' && Number.isFinite(value) ? Timestamp.fromMillis(value) : null;
}

export function serializeMatchEvent(event: MatchEvent): SerializedMatchEvent {
  return {
    ...event,
    rounds: event.rounds.map((round) => ({
      ...round,
      completedAt: toMillis(round.completedAt),
      gameResults: round.gameResults.map((game) => ({
        ...game,
        completedAt: toMillis(game.completedAt),
      })),
    })),
    startedAt: event.startedAt.toMillis(),
    updatedAt: event.updatedAt.toMillis(),
    completedAt: toMillis(event.completedAt),
  };
}

export function deserializeMatchEvent(raw: unknown): MatchEvent | null {
  if (!raw || typeof raw !== 'object') return null;
  const candidate = raw as SerializedMatchEvent;
  if (candidate.v !== MATCH_EVENT_VERSION) return null;
  if (!candidate.id || !Array.isArray(candidate.rounds)) return null;

  try {
    return {
      ...candidate,
      setCode: typeof candidate.setCode === 'string' && candidate.setCode.trim()
        ? candidate.setCode.trim().toUpperCase()
        : null,
      myDeckId: candidate.myDeckId ?? null,
      myDeckTitle: candidate.myDeckTitle ?? null,
      myLegendName: candidate.myLegendName ?? null,
      myLegendImageUrl: candidate.myLegendImageUrl ?? null,
      rounds: candidate.rounds.map((round) => ({
        ...round,
        completedAt: fromMillis(round.completedAt),
        gameResults: (round.gameResults ?? []).map((game) => ({
          ...game,
          completedAt: fromMillis(game.completedAt) ?? Timestamp.now(),
        })),
      })),
      startedAt: Timestamp.fromMillis(candidate.startedAt),
      updatedAt: Timestamp.fromMillis(candidate.updatedAt),
      completedAt: fromMillis(candidate.completedAt),
    };
  } catch {
    return null;
  }
}

export function readMatchEventDraft(): MatchEvent | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(MATCH_EVENT_DRAFT_STORAGE_KEY);
    if (!raw) return null;
    return deserializeMatchEvent(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function writeMatchEventDraft(event: MatchEvent): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      MATCH_EVENT_DRAFT_STORAGE_KEY,
      JSON.stringify(serializeMatchEvent(event)),
    );
  } catch {
    // Quota or private-mode failures must not break an in-progress event.
  }
}

export function clearMatchEventDraft(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(MATCH_EVENT_DRAFT_STORAGE_KEY);
  } catch {
    // Ignore.
  }
}

export function newMatchEventId(): string {
  return `event-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}
